import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
  TextField,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import {
  useStripeAutoTopUpControllerReadQuery,
  useStripeAutoTopUpControllerUpdateMutation,
  useStripeAutoTopUpControllerSetupMutation,
  useStripeAutoTopUpControllerEnableMutation,
  useStripeAutoTopUpControllerDisableMutation,
  useStripeAutoTopUpControllerPaymentMutation,
  useStripeAutoTopUpControllerCancelMutation,
} from '@shared/services/apiService';

import { billingErrorMessage, hostedBillingUrl } from '../model/billingError';
import { billingDate, formatBillingMoney, formatCreditUnits } from '../model/formatBillingUnits';

export const topUpBudgetMinor = (value: string): string | null => {
  if (!/^\d{2,3}(\.\d{1,2})?$/.test(value)) return null;
  const [whole, fraction = ''] = value.split('.');
  const minor = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  return minor >= 7500n && minor <= 30000n ? minor.toString() : null;
};
const formatBillingUnitsForInput = (minor: string) =>
  `${BigInt(minor) / 100n}.${(BigInt(minor) % 100n).toString().padStart(2, '0')}`;
export const BillingAutoTopUp = ({
  companyId,
  onRefresh,
  refreshSignal = 0,
}: {
  companyId: string;
  onRefresh: () => void;
  refreshSignal?: number;
}) => {
  const settings = useStripeAutoTopUpControllerReadQuery(
    { companyId },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      pollingInterval: 15000,
      skipPollingIfUnfocused: true,
    },
  );
  const { refetch } = settings;
  const [update, updateState] = useStripeAutoTopUpControllerUpdateMutation();
  const [threshold, setThreshold] = useState('100'),
    [budget, setBudget] = useState('300'),
    [editing, setEditing] = useState(false);
  const [reviewTerms, setReviewTerms] = useState<{
    configId: string | null;
    offerId: string;
    thresholdCredits: string;
    budgetMinor: string;
  } | null>(null);
  const [setup, setupState] = useStripeAutoTopUpControllerSetupMutation();
  const [enable, enableState] = useStripeAutoTopUpControllerEnableMutation();
  const [disable, disableState] = useStripeAutoTopUpControllerDisableMutation();
  const [payment, paymentState] = useStripeAutoTopUpControllerPaymentMutation();
  const [cancel, cancelState] = useStripeAutoTopUpControllerCancelMutation();
  const [search] = useSearchParams();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [review, setReview] = useState(false),
    [accepted, setAccepted] = useState(false);
  const [confirmSetupId, setConfirmSetupId] = useState<string | null>(null);
  const [cancelReview, setCancelReview] = useState(false);
  const [error, setError] = useState<string | null>(null),
    [message, setMessage] = useState<string | null>(null);
  const active = useRef(false),
    handledReturn = useRef(false);
  const keys = useRef(new Map<string, string>()),
    lastSignal = useRef(refreshSignal),
    lastFacts = useRef<string | null>(null);
  const data = settings.data;
  const busy =
    updateState.isLoading ||
    setupState.isLoading ||
    enableState.isLoading ||
    disableState.isLoading ||
    paymentState.isLoading ||
    cancelState.isLoading;
  const policyThreshold = data?.thresholdCredits,
    policyBudget = data?.budgetMinor;
  useEffect(() => {
    if (settingsOpen && policyThreshold && policyBudget) {
      setThreshold(policyThreshold);
      setBudget(formatBillingUnitsForInput(policyBudget));
    }
  }, [settingsOpen, policyThreshold, policyBudget]);
  const budgetMinor = topUpBudgetMinor(budget);
  const belowExposure =
    budgetMinor !== null &&
    !!data &&
    BigInt(budgetMinor) < BigInt(data.spentMinor) + BigInt(data.reservedMinor);
  const valid =
    /^[1-9]\d{0,2}$/.test(threshold) &&
    Number(threshold) <= 500 &&
    budgetMinor !== null &&
    !belowExposure;
  const changed = threshold !== data?.thresholdCredits || budgetMinor !== data?.budgetMinor;
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  useEffect(() => {
    if (lastSignal.current !== refreshSignal) {
      lastSignal.current = refreshSignal;
      void refetch();
    }
  }, [refreshSignal, refetch]);
  useEffect(() => {
    if (!data) return;
    const signature = JSON.stringify([
      data.enabled,
      data.spentMinor,
      data.reservedMinor,
      data.pending?.purchaseId,
      data.pending?.state,
      data.paused,
    ]);
    const changed = lastFacts.current !== null && lastFacts.current !== signature;
    lastFacts.current = signature;
    if (changed) onRefresh();
  }, [data, onRefresh]);
  const returnKind = search.get('autotopup'),
    returnedSetup = search.get('setup');
  useEffect(() => {
    if (returnKind === 'success' && returnedSetup && !handledReturn.current) {
      handledReturn.current = true;
      setConfirmSetupId(returnedSetup);
      setAccepted(false);
      setReview(true);
    }
  }, [returnKind, returnedSetup]);
  const key = (action: string) => {
    const existing = keys.current.get(action);
    if (existing) return existing;
    const value = crypto.randomUUID();
    keys.current.set(action, value);
    return value;
  };
  const refresh = () => {
    void refetch();
    onRefresh();
  };
  const save = async () => {
    if (
      !accepted ||
      !data?.canManage ||
      !data.canEnable ||
      !data.offer ||
      (confirmSetupId && confirmSetupId !== data.readySetupId)
    )
      return;
    setError(null);
    try {
      if (editing) {
        if (!reviewTerms?.configId) return;
        await update({
          companyId,
          autoTopUpUpdateDto: {
            configId: reviewTerms.configId,
            thresholdCredits: reviewTerms.thresholdCredits,
            budgetMinor: reviewTerms.budgetMinor,
            requestKey: key(
              `settings:${reviewTerms.configId}:${reviewTerms.thresholdCredits}:${reviewTerms.budgetMinor}`,
            ),
            acceptTerms: true,
          },
        }).unwrap();
        if (!active.current) return;
        setReview(false);
        setSettingsOpen(false);
        setMessage(
          'Automatic top-up settings saved. Spending already recorded in this paid period is unchanged.',
        );
        refresh();
        return;
      }
      if (confirmSetupId) {
        const updated = await enable({
          companyId,
          autoTopUpConfirmDto: {
            setupId: confirmSetupId,
            requestKey: key(`enable:${confirmSetupId}`),
            acceptTerms: true,
          },
        }).unwrap();
        if (!active.current) return;
        keys.current.delete(`enable:${confirmSetupId}`);
        setReview(false);
        setMessage(
          updated.enabled
            ? 'Automatic top-up is enabled for this company.'
            : 'These settings changed. Automatic top-up remains off; start a fresh setup to enable it.',
        );
        refresh();
      } else {
        if (!reviewTerms) return;
        const result = await setup({
          companyId,
          autoTopUpSetupDto: {
            offerId: reviewTerms.offerId,
            requestKey: key(
              `setup:${reviewTerms.offerId}:${reviewTerms.thresholdCredits}:${reviewTerms.budgetMinor}`,
            ),
            thresholdCredits: reviewTerms.thresholdCredits,
            budgetMinor: reviewTerms.budgetMinor,
            acceptTerms: true,
          },
        }).unwrap();
        if (!active.current) return;
        keys.current.delete(`setup:${data.offer.id}`);
        if (result.state === 'open' && result.url)
          window.location.assign(hostedBillingUrl(result.url, 'checkout'));
        else if (result.state === 'ready') {
          setConfirmSetupId(result.setupId);
          setAccepted(false);
          void refetch();
        } else setError('This card setup expired. Close this dialog and start again.');
      }
    } catch (e) {
      if (active.current) {
        setError(billingErrorMessage(e));
        void refetch();
      }
    }
  };
  const turnOff = async () => {
    setError(null);
    try {
      const updated = await disable({
        companyId,
        autoTopUpRequestDto: { requestKey: key('disable') },
      }).unwrap();
      if (!active.current) return;
      keys.current.delete('disable');
      setSettingsOpen(false);
      setMessage(
        updated.enabled
          ? 'Automatic top-up is still enabled. Reload the page and try again.'
          : 'New automatic purchases are turned off. Already initiated payments still need confirmation or cancellation.',
      );
      refresh();
    } catch (e) {
      if (active.current) setError(billingErrorMessage(e));
    }
  };
  const completePayment = async () => {
    if (!data?.pending) return;
    setError(null);
    try {
      const result = await payment({ companyId, purchaseId: data.pending.purchaseId }).unwrap();
      if (!active.current) return;
      if (result.url) window.location.assign(hostedBillingUrl(result.url, 'invoice'));
      else {
        setMessage(
          result.state === 'succeeded'
            ? 'Payment confirmed and credits added.'
            : 'Payment is not yet confirmed. Status updates automatically.',
        );
        refresh();
      }
    } catch (e) {
      if (active.current) setError(billingErrorMessage(e));
    }
  };
  const cancelPurchase = async () => {
    if (!data?.pending) return;
    setError(null);
    try {
      const result = await cancel({
        companyId,
        purchaseId: data.pending.purchaseId,
        autoTopUpRequestDto: { requestKey: key(`cancel:${data.pending.purchaseId}`) },
      }).unwrap();
      if (!active.current) return;
      keys.current.delete(`cancel:${data.pending.purchaseId}`);
      setCancelReview(false);
      const purchaseMessage =
        result.state === 'succeeded'
          ? 'The payment had already completed. Its credits were added.'
          : result.state === 'expired'
            ? 'The pending purchase was cancelled.'
            : 'This payment still needs confirmation or billing operator review.';
      setMessage(
        result.automaticEnabled === undefined
          ? purchaseMessage
          : `${purchaseMessage} ${result.automaticEnabled ? 'Automatic top-up is currently enabled.' : 'New automatic purchases are turned off.'}`,
      );
      refresh();
    } catch (e) {
      if (active.current) {
        setError(billingErrorMessage(e));
        refresh();
      }
    }
  };

  if (settings.isLoading) return <CircularProgress aria-label="Loading automatic top-up" />;
  if (settings.isError || !data)
    return (
      <Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>
        Automatic top-up settings could not be loaded.
      </Alert>
    );
  const money = (units: string) => formatBillingMoney(units, data.currency, data.currencyScale);
  const snapshotTerms = () =>
    data.offer && budgetMinor
      ? {
          configId: data.configId ?? null,
          offerId: data.offer.id,
          thresholdCredits: threshold,
          budgetMinor,
        }
      : null;
  const openReview = (setupId: string | null = null) => {
    setReviewTerms(snapshotTerms());
    setSettingsOpen(false);
    if (setupId) keys.current.delete(`enable:${setupId}`);
    else if (data.offer) keys.current.delete(`setup:${data.offer.id}`);
    setConfirmSetupId(setupId);
    setAccepted(false);
    setError(null);
    setReview(true);
  };
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Button
        variant="outlined"
        disabled={busy}
        aria-haspopup="dialog"
        onClick={() => setSettingsOpen(true)}>
        Auto top-up: {data.enabled ? (data.paused ? 'Paused' : 'On') : 'Off'}
      </Button>
      {data.enabled && (
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }}>
          {money(data.spentMinor)} spent · {money(data.reservedMinor)} pending ·{' '}
          {money(data.remainingMinor)} remaining of {money(data.budgetMinor)} per paid period
        </Typography>
      )}
      {message && <Alert severity="info">{message}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {returnKind === 'cancel' && (
        <Alert severity="info">
          Card setup was closed. Auto top-up was not enabled by returning to this page.
        </Alert>
      )}
      {data.enabled && data.paused && (
        <Alert severity="warning">
          Automatic purchases are paused until paid access and the approved package are available.
        </Alert>
      )}
      {data.pending && (
        <Alert severity={data.pending.needsAttention ? 'warning' : 'info'}>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              {data.pending.needsAttention
                ? 'An automatic purchase needs attention. Credits are added only after confirmed payment.'
                : 'An automatic purchase is being prepared or verified. Its amount is reserved against your budget.'}
            </Typography>
            {data.canManage && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button disabled={busy} onClick={() => void completePayment()}>
                  Complete payment
                </Button>
                <Button
                  disabled={busy}
                  onClick={() => {
                    setError(null);
                    setCancelReview(true);
                  }}>
                  Cancel purchase and turn off
                </Button>
              </Stack>
            )}
          </Stack>
        </Alert>
      )}

      <Dialog
        open={settingsOpen}
        fullWidth
        maxWidth="sm"
        aria-labelledby="auto-top-up-settings-title"
        onClose={() => {
          if (!busy) setSettingsOpen(false);
        }}>
        <DialogTitle id="auto-top-up-settings-title">Automatic top-up</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              When available credits fall below {threshold || '…'}, buy{' '}
              {data.offer ? formatCreditUnits(data.offer.units, data.offer.creditScale) : '500'}{' '}
              credits for{' '}
              {data.offer
                ? formatBillingMoney(
                    data.offer.amountMinor,
                    data.offer.currency,
                    data.offer.currencyScale,
                  )
                : 'USD 75.00'}
              .
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Top up when below (credits)"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                disabled={busy || !data.canManage || !data.canEnable}
                helperText="1–500 credits"
                inputMode="numeric"
              />
              <TextField
                label="Automatic budget (USD / period)"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                disabled={busy || !data.canManage || !data.canEnable}
                helperText="USD 75–300; pending purchases count"
                inputMode="decimal"
              />
            </Stack>
            {!valid && (
              <Alert severity="info">
                {belowExposure
                  ? 'The proposed budget cannot be below spending plus pending purchases.'
                  : 'Enter a threshold from 1 to 500 credits and a budget from USD 75 to 300.'}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              Maximum automatic purchases: {money(budgetMinor ?? data.budgetMinor)} per paid period.
              Pending purchases count toward this limit; manual purchases are separate.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {data.enabled && data.canManage && (
              <Button disabled={busy} onClick={() => void turnOff()}>
                Turn off automatic top-up
              </Button>
            )}
            <Box
              component="dl"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
                gap: 1,
                '& dt': { typography: 'body2', color: 'text.secondary' },
                '& dd': { m: 0, typography: 'body2', textAlign: 'right', overflowWrap: 'anywhere' },
              }}>
              <Box component="dt">Current automatic budget</Box>
              <Box component="dd">{money(data.budgetMinor)}</Box>
              <Box component="dt">Automatic spending</Box>
              <Box component="dd">{money(data.spentMinor)}</Box>
              <Box component="dt">Pending purchases</Box>
              <Box component="dd">{money(data.reservedMinor)}</Box>
              <Box component="dt">Current budget remaining</Box>
              <Box component="dd">{money(data.remainingMinor)}</Box>
              <Box component="dt">Proposed automatic budget</Box>
              <Box component="dd">{budgetMinor ? money(budgetMinor) : 'Enter a valid budget'}</Box>
              <Box component="dt">Remaining after save</Box>
              <Box component="dd">
                {budgetMinor
                  ? money(
                      (BigInt(budgetMinor) > BigInt(data.spentMinor) + BigInt(data.reservedMinor)
                        ? BigInt(budgetMinor) - BigInt(data.spentMinor) - BigInt(data.reservedMinor)
                        : 0n
                      ).toString(),
                    )
                  : '—'}
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Your monthly allowance is used first; top-up credits never expire. Your plan, renewal
              date and call capacity stay the same.{' '}
              {data.periodEndsAt
                ? `Current paid period ends ${billingDate(data.periodEndsAt)}.`
                : 'An active paid period is required.'}
            </Typography>
            {!data.canEnable && (
              <Typography variant="body2" color="text.secondary">
                {data.enabled
                  ? 'Automatic purchases are paused until paid access and the approved package are available.'
                  : 'An active paid period and an available Boost package are required to enable automatic top-up.'}
              </Typography>
            )}
            {!data.canManage && (
              <Typography variant="body2" color="text.secondary">
                A billing manager can change these settings.
              </Typography>
            )}
            {data.canManage && data.canEnable && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {data.enabled && (
                  <Button
                    variant="contained"
                    disabled={busy || !valid || !changed || !!data.pending}
                    onClick={() => {
                      setReviewTerms(snapshotTerms());
                      setEditing(true);
                      setAccepted(false);
                      setConfirmSetupId(null);
                      setError(null);
                      setReview(true);
                    }}>
                    Review changes
                  </Button>
                )}
                <Button
                  variant="outlined"
                  disabled={busy || !valid}
                  onClick={() => {
                    setEditing(false);
                    openReview();
                  }}>
                  {data.enabled ? 'Update saved card' : 'Set up automatic top-up'}
                </Button>
                {data.readySetupId && (
                  <Button
                    disabled={busy}
                    onClick={() => {
                      setEditing(false);
                      openReview(data.readySetupId);
                    }}>
                    Confirm saved card
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setSettingsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={review}
        fullWidth
        maxWidth="sm"
        onClose={() => {
          if (!busy) setReview(false);
        }}>
        <DialogTitle>
          {confirmSetupId ? 'Confirm automatic top-up' : 'Authorize automatic top-up'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {confirmSetupId && confirmSetupId !== data.readySetupId && (
              <Alert severity="warning">
                This card setup is no longer current. Close this review and start a fresh setup.
              </Alert>
            )}
            <Typography>
              {confirmSetupId
                ? (data.readySetupConsentText ?? data.consentText)
                : `Authorize Callevate to charge USD 75 for 500 credits when your available balance is below ${reviewTerms?.thresholdCredits} credits, up to USD ${reviewTerms ? formatBillingUnitsForInput(reviewTerms.budgetMinor) : 'unavailable'} per paid subscription period. Pending automatic purchases count towards this budget. Top-up credits never expire. Already initiated payments will still be reconciled.`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {editing
                ? 'Confirm these limits for the currently saved card. Spending already recorded in this paid period is retained.'
                : confirmSetupId
                  ? 'Confirm to enable automatic purchases for your company using the card saved with Stripe.'
                  : 'Save your card securely with Stripe, then return here to confirm activation. Saving the card alone does not enable automatic purchases.'}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  disabled={busy}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
              }
              label="I authorize these automatic charges for this company."
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setReview(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !accepted ||
              busy ||
              !data.canManage ||
              !data.canEnable ||
              (!confirmSetupId && !reviewTerms) ||
              (!!confirmSetupId && confirmSetupId !== data.readySetupId)
            }
            onClick={() => void save()}>
            {busy
              ? 'Please wait…'
              : editing
                ? 'Confirm settings'
                : confirmSetupId
                  ? 'Enable auto top-up'
                  : 'Save card in Stripe'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={cancelReview}
        fullWidth
        maxWidth="sm"
        onClose={() => {
          if (!busy) setCancelReview(false);
        }}>
        <DialogTitle>Cancel pending automatic purchase?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              Future automatic purchases will be turned off. The pending budget is released only
              after Stripe confirms cancellation. A payment that already completed will still add
              its purchased credits.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setCancelReview(false)}>
            Keep purchase
          </Button>
          <Button disabled={busy} color="error" onClick={() => void cancelPurchase()}>
            Cancel purchase and turn off
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
